<script setup lang="ts">
import type { BlockCardNodeViewProps } from "./types";
import type AxureInlineCard from "./well-known/AxureInlineCard.vue";
import { useQuery } from "@tanstack/vue-query";
import { NodeViewWrapper } from "@tiptap/vue-3";
import { computedWithControl, useToggle, whenever } from "@vueuse/core";
import FormattedDate from "ls/components/FormattedDate.vue";
import SimpleButton from "ls/components/simple-button/SimpleButton.vue";
import { SimplePopoverAnchor, SimplePopoverContent, SimplePopoverPortal, SimplePopoverRoot, SimplePopoverTrigger } from "ls/components/simple-popover";
import { useIsEmbed } from "ls/composables/useIsEmbed";
import { queries } from "ls/features/bookmark/queries";
import { useMetadataDetails } from "ls/features/bookmark/useMetadataDetails";
import ActualizeLinkButton from "ls/features/disko/links/ActualizeLinkButton.vue";
import LinkFrame from "ls/features/disko/links/LinkFrame.vue";
import ContentResizer from "ls/features/disko/rich-text/block-controls/ContentResizer.vue";
import { BLOCK_CONTENT_ATTRIBUTE } from "ls/features/disko/rich-text/block-controls/types";
import { UniqueIDAttributeName, UniqueIDDataAttributeName } from "ls/features/disko/rich-text/extensions/UniqueId";
import { isEmbeddingAllowed, toProjectType } from "ls/features/files/utils";
import { PlayIcon, StopCircleIcon } from "lucide-vue-next";
import { computed, ref, toRef, watch } from "vue";
import LinkToolbar from "../LinkToolbar.vue";
import LinkToolbarEdit from "../LinkToolbarEdit.vue";
import MaximizeOverlayContainer from "../MaximizeOverlayContainer.vue";
import { useLinkNode } from "../useLinkNode";
import { useWellKnownServiceMatch } from "./well-known/providers";

const props = defineProps<BlockCardNodeViewProps>();

const [isPlaying, togglePlaying] = useToggle(false);
const ignoreUrlChangeOnce = ref(false);
const url = toRef(() => props.node.attrs.url);
const showUpdated = ref(false);
const iframeReportedUrl = ref<string>();

const { isMaximized, showToolbar, handleUpdateOpen, close, isEditing } = useLinkNode(props);
const isEmbed = useIsEmbed();
const { data: urlMetadata } = useQuery(computed(() => queries.urlMetadata(props.node.attrs.url)));
const { faviconSrc } = useMetadataDetails(urlMetadata);

// use well known components for header block rendering
const wellKnownServiceMatch = useWellKnownServiceMatch(url);
const wellKnownInlineCard = ref<InstanceType<typeof AxureInlineCard>>();
const text = computed(() => {
  if (wellKnownInlineCard.value && wellKnownInlineCard.value.label) {
    return wellKnownInlineCard.value.label as string;
  }
  if (urlMetadata.value && urlMetadata.value.meta?.title) {
    return urlMetadata.value.meta.title;
  }
  return props.node.attrs.text || props.node.attrs.url;
});

const frameUrl = computedWithControl(() => isPlaying.value, () => {
  return urlMetadata.value?.embedUrl ?? props.node.attrs.url;
});

const width = ref(props.node.attrs.width);
const aspectRatio = ref(props.node.attrs.aspectRatio);

watch(
  () => props.node.attrs.width,
  newWidth => {
    if (newWidth !== width.value) width.value = newWidth;
  },
);

watch(
  () => props.node.attrs.aspectRatio,
  newRatio => {
    if (newRatio !== aspectRatio.value) aspectRatio.value = newRatio;
  },
);

watch([width, aspectRatio], ([widthValue, aspectRatioValue]) => {
  props.editor
    .chain()
    .setBlockWidth(widthValue)
    .setBlockAspectRatio(aspectRatioValue)
    .run();
});

const allowEmbed = computed(() => {
  const fileType = urlMetadata.value?.axureFileType;
  if (!fileType) return true;
  const projectType = toProjectType(fileType);
  return projectType !== undefined && isEmbeddingAllowed(projectType);
});

const allowPlaying = computed(() => !isEmbed.value && allowEmbed.value);

// start playing when the card is maximized
whenever(isMaximized, () => {
  togglePlaying(true);
}, { immediate: true });

// stop playing when url has changed
watch(url, () => {
  if (!ignoreUrlChangeOnce.value) {
    togglePlaying(false);
  } else {
    ignoreUrlChangeOnce.value = false;
  }
});

watch(isPlaying, value => {
  if (!value) {
    iframeReportedUrl.value = "";
  }
});

function handleUnlink() {
  const pos = props.getPos();
  if (pos === undefined) return false;

  props.editor
    .chain()
    .setNodeSelection(pos)
    .unlinkBlockCard(text.value)
    .focus()
    .run();
}

function setSelected() {
  const pos = props.getPos();
  if (pos === undefined) return false;
  props.editor.commands.setNodeSelection(pos);
}

function actualizeLink() {
  const url = iframeReportedUrl.value;
  if (props.node.attrs.url === url) return;
  ignoreUrlChangeOnce.value = true;
  props.updateAttributes({
    url,
  });
  showUpdated.value = true;
}
</script>

<template>
  <NodeViewWrapper
    v-bind="{
      [BLOCK_CONTENT_ATTRIBUTE]: '',
      [UniqueIDDataAttributeName]: node.attrs[UniqueIDAttributeName],
    }"
    :data-content-type="props.node.type.name"
  >
    <SimplePopoverRoot
      :open="showToolbar"
      :modal="false"
      @update:open="handleUpdateOpen"
    >
      <!-- use 1px anchor for popover content sizing to be minimal -->
      <SimplePopoverAnchor as="span" class="size-px" />
      <SimplePopoverTrigger as-child>
        <ContentResizer
          v-model:width="width"
          v-model:aspect-ratio="aspectRatio"
          allow-aspect-ratio-change
          @pointer-down="setSelected"
        >
          <div
            class="relative -top-px flex size-full cursor-default flex-col overflow-hidden rounded-md border px-2.5 pt-3 pb-2.5"
          >
            <div class="mb-2 flex flex-col px-1.5">
              <div class="flex w-full flex-nowrap items-start text-sm">
                <component
                  :is="wellKnownServiceMatch.inlineCard"
                  v-if="wellKnownServiceMatch"
                  ref="wellKnownInlineCard"
                  v-bind="props"
                  :match="wellKnownServiceMatch"
                  :metadata="urlMetadata"
                  block-card
                />
                <template v-else>
                  <img v-if="faviconSrc" class="relative top-0.5 mr-1 inline size-4 flex-none" :src="faviconSrc">
                  <span class="truncate">{{ props.node.attrs.text || urlMetadata?.meta?.title || props.node.attrs.url }}</span>
                </template>
              </div>
              <div class="flex w-full flex-nowrap items-center text-xs font-light">
                <div class="flex-1 truncate pt-2 text-slate-400">
                  <span>{{ urlMetadata?.meta?.siteName ? urlMetadata?.meta?.siteName : props.node.attrs.url }}</span>
                  <FormattedDate v-if="urlMetadata?.meta?.modifiedDate" v-slot="{ short }" :value="urlMetadata.meta.modifiedDate">
                    <span>{{ ` | Updated ${short}` }}</span>
                  </FormattedDate>
                </div>
                <div class="flex gap-2">
                  <ActualizeLinkButton
                    v-if="isPlaying && iframeReportedUrl"
                    v-model:show-updated="showUpdated"
                    @actualize-link="actualizeLink"
                  />
                  <SimpleButton
                    v-if="isPlaying"
                    variant="destructive"
                    @click.stop="togglePlaying(false)"
                  >
                    <StopCircleIcon class="size-4" />
                    Close
                  </SimpleButton>
                </div>
              </div>
            </div>
            <div
              class="relative flex size-full items-center justify-center overflow-hidden border bg-white"
              :class="{ 'aspect-video': !node.attrs.aspectRatio,
                        'z-10': isPlaying }"
            >
              <MaximizeOverlayContainer
                v-if="isPlaying"
                v-model="isMaximized"
              >
                <LinkFrame
                  v-model:current-url="iframeReportedUrl"
                  :url="frameUrl"
                />
              </MaximizeOverlayContainer>
              <template v-else>
                <img v-if="urlMetadata?.meta?.image" :src="urlMetadata.meta.image" class="max-h-full">
                <SimpleButton
                  size="auto"
                  variant="ghost"
                  class="absolute rounded-full! bg-slate-400/50 p-6 hover:[&:not([data-disabled])]:bg-slate-900/50"
                  @click="allowPlaying && togglePlaying(true)"
                >
                  <PlayIcon fill="#fff" class="size-12 text-white" aria-hidden="true" />
                </SimpleButton>
              </template>
            </div>
          </div>
        </ContentResizer>
      </SimplePopoverTrigger>
      <SimplePopoverPortal>
        <SimplePopoverContent
          side="top"
          align="start"
          data-axure-bubble-menu-content
          @open-auto-focus.prevent
        >
          <LinkToolbarEdit
            v-if="isEditing"
            v-bind="props"
            @close="close"
          />
          <LinkToolbar
            v-else
            v-bind="props"
            v-model:maximized="isMaximized"
            v-model:edit-mode="isEditing"
            @unlink="handleUnlink"
          />
        </SimplePopoverContent>
      </SimplePopoverPortal>
    </SimplePopoverRoot>
  </NodeViewWrapper>
</template>
